"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from "react";
import { usePathname } from "next/navigation";
import {
  UPPL_COOKIE,
  UPPL_ROST_STORAGE_KEY,
  formateraUpplasningCookie,
  type UpplasningPref,
} from "@/lib/upplasning-constants";
import { stoppa } from "@/lib/upplasning/tal";

// --- Device-bound voice choice, as an external store (localStorage). ---
// Modeled as a useSyncExternalStore rather than a useEffect+useState pair:
// the value lives outside React (localStorage), must render identically on
// the server (no localStorage there) and on the client's first paint before
// hydration reads it, and may be written from outside the component that
// reads it. useEffect+setState reads it a tick late instead, which is both
// an extra render and — per the framework's own React Compiler lint —
// exactly the kind of "sync external state into React" job this hook exists
// to replace.
let rostURICache: string | null = null;
let rostURIHydrated = false;
const rostURIListeners = new Set<() => void>();

function emitRostURI(): void {
  for (const listener of rostURIListeners) listener();
}

function subscribeRostURI(listener: () => void): () => void {
  rostURIListeners.add(listener);
  return () => {
    rostURIListeners.delete(listener);
  };
}

// Must return a stable reference when nothing changed, or React re-renders
// forever — so the localStorage read happens once (lazily, on first client
// call) and every later call returns the cached value.
function getRostURISnapshot(): string | null {
  if (!rostURIHydrated) {
    rostURIHydrated = true;
    try {
      rostURICache = window.localStorage.getItem(UPPL_ROST_STORAGE_KEY);
    } catch {
      // Disabled storage — stay on the device default.
      rostURICache = null;
    }
  }
  return rostURICache;
}

// The server has no device-bound preference at all.
function getRostURIServerSnapshot(): string | null {
  return null;
}

function setRostURI(next: string | null): void {
  try {
    if (next) window.localStorage.setItem(UPPL_ROST_STORAGE_KEY, next);
    else window.localStorage.removeItem(UPPL_ROST_STORAGE_KEY);
  } catch {
    // Disabled storage — the choice simply does not survive a reload.
  }
  rostURICache = next;
  rostURIHydrated = true;
  emitRostURI();
}

interface UpplasningContextValue {
  pa: boolean;
  hastighet: number;
  /** Device-bound voice choice; null = whatever the device offers first. */
  rostURI: string | null;
  setPa: (pa: boolean) => void;
  setHastighet: (hastighet: number) => void;
  setRostURI: (rostURI: string | null) => void;
}

const UpplasningContext = createContext<UpplasningContextValue | null>(null);

interface UpplasningProviderProps {
  initial: UpplasningPref;
  children: ReactNode;
}

export function UpplasningProvider({ initial, children }: UpplasningProviderProps) {
  const [pref, setPref] = useState<UpplasningPref>(initial);
  // Shadow copy of `pref`, updated imperatively (not through React's render
  // cycle). setPa() and setHastighet() each only patch one field, and both
  // can be called synchronously in the same event handler before React has
  // re-rendered — if they derived their patch from the `pref` closed over at
  // render time, the second call would merge against the pre-first-call
  // value and silently discard it. Reading/writing prefRef.current instead
  // means each call sees the other's result immediately, regardless of
  // React's batching. (A plain `setPref(prev => ...)` updater would fix the
  // *state* half of this on its own, but the resolved value is also needed
  // — exactly once — for the cookie mirror and the PATCH below; deriving it
  // inside an updater and reusing it outside doesn't work, since updaters
  // must stay pure and React may invoke one more than once.)
  const prefRef = useRef<UpplasningPref>(initial);
  const rostURI = useSyncExternalStore(
    subscribeRostURI,
    getRostURISnapshot,
    getRostURIServerSnapshot,
  );

  // Never leave speech running behind a closed tab.
  useEffect(() => {
    const stanna = () => stoppa();
    window.addEventListener("pagehide", stanna);
    return () => {
      window.removeEventListener("pagehide", stanna);
      stoppa();
    };
  }, []);

  // Never leave speech running behind a client-side (App Router) navigation
  // either. pagehide above only fires on a full unload/tab-close, and this
  // provider lives in the root layout so it never unmounts on a <Link>
  // navigation — usePathname is the signal that a new page has taken over.
  const pathname = usePathname();
  const forstaRender = useRef(true);
  useEffect(() => {
    if (forstaRender.current) {
      forstaRender.current = false;
      return;
    }
    stoppa();
  }, [pathname]);

  const spara = useCallback((patch: Partial<UpplasningPref>) => {
    const next: UpplasningPref = { ...prefRef.current, ...patch };
    prefRef.current = next;
    setPref(next);
    if (!next.pa) stoppa();
    if (typeof document !== "undefined") {
      const oneYear = 60 * 60 * 24 * 365;
      document.cookie = `${UPPL_COOKIE}=${formateraUpplasningCookie(next)}; path=/; max-age=${oneYear}; samesite=lax`;
    }
    // Cross-device persistence. Fails silently (401, offline) — the local
    // update above already gave the user feedback, and the cookie carries the
    // choice on this device regardless.
    if (typeof fetch !== "undefined") {
      fetch("/api/user/upplasning", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(next),
        credentials: "same-origin",
      }).catch(() => {
        // Ignore — local state is the source of truth for this device.
      });
    }
  }, []);

  const setPa = useCallback((nyPa: boolean) => spara({ pa: nyPa }), [spara]);
  const setHastighet = useCallback((ny: number) => spara({ hastighet: ny }), [spara]);

  return (
    <UpplasningContext.Provider
      value={{ pa: pref.pa, hastighet: pref.hastighet, rostURI, setPa, setHastighet, setRostURI }}
    >
      {children}
    </UpplasningContext.Provider>
  );
}

export function useUpplasning(): UpplasningContextValue {
  const ctx = useContext(UpplasningContext);
  if (!ctx) {
    throw new Error("useUpplasning must be used within UpplasningProvider");
  }
  return ctx;
}
